import React, { useRef, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

import { useSession } from '../context';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import { MessageType, Message, BotAgentMessage } from '@tomis-tech/types';
import CheckAvailability from './CheckAvailability';
import SmsForm from './SmsForm';

// const typingSrc = new URL("../images/typing.gif", import.meta.url);

interface ChatWindowMessagesProps {
  messages: Message[];
}

const ChatWindowMessages: React.FC<ChatWindowMessagesProps> = React.memo(
  ({ messages }) => {
    const { conversation, site } = useSession();
    const scrollContainerRef = useRef(null);
    const { checkAvailability } = conversation.value;

    const isCheckingAvailability =
      Boolean(checkAvailability) && checkAvailability.active;

    const latestMessageIsBot =
      messages[0] && messages[0].type === MessageType.BotAgent;

    /** Dialogflow Action of most recent bot message */
    const action: string =
      (latestMessageIsBot &&
        (messages[0] as BotAgentMessage).dialogflowQueryResult &&
        (messages[0] as BotAgentMessage).dialogflowQueryResult.action) ||
      '';

    // Scroll to bottom when new messages are received
    useEffect(() => {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }, [messages.length]);

    /** Scroll to the bottom of messages */
    function scrollToBottom() {
      scrollContainerRef.current.scrollTop =
        scrollContainerRef.current.scrollHeight;
    }

    const smsTransitionSupported = site.features.includes('liveChatSMS');
    const webchatTransitionSupported =
      !smsTransitionSupported && site.features.includes('liveChatWeb');

    return (
      <div
        css={{
          flexGrow: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <ul
          ref={scrollContainerRef}
          css={{
            flexGrow: 1,
            backgroundColor: 'white',
            overflowY: 'scroll',
            scrollBehavior: 'smooth',
            padding: '15px 15px 5px 15px',
            listStyleType: 'none',
            display: 'flex',
            flexDirection: 'column-reverse',
          }}
        >
          {messages.map(message => {
            return <ChatMessage key={message.id} message={message} />;
          })}
        </ul>
        {isCheckingAvailability && (
          <CheckAvailability scrollToBottom={scrollToBottom} />
        )}
        <ChatInput
          showYesNo={
            (smsTransitionSupported || webchatTransitionSupported) &&
            action === 'speak_to_human'
          }
        />
        <FormTransition />
      </div>
    );
  }
);

export default ChatWindowMessages;

type Animation = 'up' | 'down' | 'none';

/** SMS Form w/ wrapper for animation (slide up, slide down) */
const FormTransition: React.FC = () => {
  const [showContent, setShowContent] = useState(false);
  const { conversation, site } = useSession();

  const { conversationMode } = conversation.value;
  const botMode = site.currentMode;

  const animation: Animation =
    botMode === 'liveChatSMS'
      ? 'none'
      : conversationMode === 'liveChatSMS'
      ? 'up'
      : 'down';

  // Show/Hide SMS form
  useEffect(() => {
    if (conversationMode !== 'liveChatSMS') {
      setTimeout(() => setShowContent(false), 500);
    } else {
      setShowContent(true);
    }
  }, [conversationMode]);

  return (
    <SlidingContent show={showContent} animation={animation}>
      <SmsForm />
    </SlidingContent>
  );
};

const SlideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`;

const SlideDown = keyframes`
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
`;

const SlidingContent = styled.div<{
  show: boolean;
  animation: Animation;
}>`
  position: absolute;
  bottom: 0;
  width: 100%;
  height: 100%;
  background: transparent;
  animation: ${({ animation }) =>
      animation === 'up' ? SlideUp : animation === 'down' ? SlideDown : 'none'}
    0.5s ease-in-out forwards;
  display: ${({ show }) => (show ? 'block' : 'none')};
  z-index: 20;
`;
